import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { WHY, STATS, IMAGES, COMPANY } from "@/data/content";

export default function WhyChooseUs() {
  return (
    <section id="why" className="py-24 bg-obsidian">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Image + stats */}
          <div className="lg:col-span-5 relative">
            <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }} transition={{ duration: 0.6 }}
              className="relative rounded-md overflow-hidden border border-white/10">
              <img src={IMAGES.trust} alt="HoneyBee roofing crew installing shingles in Snohomish County, WA" className="w-full h-[460px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 bg-black/60 backdrop-blur-md border border-white/10 rounded-sm p-4">
                <p className="text-sm text-zinc-200">
                  <span className="text-honey font-bold">{COMPANY.guarantee}</span> — backed by a partner trusted for 300+ years.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Copy + reasons */}
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.2em] text-honey font-bold mb-3">Built on Trust</p>
            <h2 className="text-4xl sm:text-5xl font-display font-black uppercase tracking-tighter text-white">
              Why Homeowners Choose HoneyBee
            </h2>
            <p className="text-zinc-400 mt-4 text-lg max-w-xl">
              We do more than install roofs — we build relationships. No pushy sales tactics, no shortcuts.
              Just high-quality craftsmanship and a team you can rely on.
            </p>

            <div className="grid sm:grid-cols-2 gap-5 mt-9">
              {WHY.map((w, i) => {
                const Icon = Icons[w.icon] || Icons.Check;
                return (
                  <motion.div key={w.title}
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }}
                    data-testid={`why-card-${i}`}
                    className="p-5 rounded-sm border border-white/10 bg-carbon hover:border-honey/40 transition-colors">
                    <div className="w-10 h-10 rounded-sm bg-honey/15 flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5 text-honey" />
                    </div>
                    <h3 className="text-base font-display font-bold text-white">{w.title}</h3>
                    <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">{w.body}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px mt-16 border border-white/10 rounded-md overflow-hidden bg-white/10">
          {STATS.map((s) => (
            <div key={s.label} className="bg-obsidian p-7 text-center">
              <div className="text-3xl sm:text-4xl font-display font-black text-honey">{s.value}</div>
              <div className="text-xs uppercase tracking-wider text-zinc-400 mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
